import React, { useEffect, useMemo, useRef, useState } from 'react';

import { BarChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { nativePrintSession, useNativePrintMode } from '~/nativeLenses/nativePrintSession';

import { Box, Slider, Typography } from '@mui/material';

import dataSplitColours from './dataSplitColours.json';
import { type Row, number } from './liveModel';
import { reportPalette } from './palette';

echarts.use([BarChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

export const dataSplitColour = (source: string) =>
  (dataSplitColours as Record<string, string>)[source] ?? reportPalette.teal;

export function regionalRanking(rows: Array<Row>, metric: string) {
  const values = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const name = String(row.split ?? 'Unknown');
    const source = String(row.detail ?? 'Unknown');
    const value = number(row[metric]);
    if (value === null) {
      continue;
    }
    const group = values.get(name) ?? new Map<string, number>();
    group.set(source, (group.get(source) ?? 0) + value);
    values.set(name, group);
  }
  const total = (group: Map<string, number>) => [...group.values()].reduce((sum, value) => sum + value, 0);
  return [...values].sort((a, b) => total(b[1]) - total(a[1]) || a[0].localeCompare(b[0]));
}

export default function RegionalRanking({ rows, metric }: { rows: Array<Row>; metric: string }) {
  const printMode = useNativePrintMode() || Boolean(nativePrintSession());
  const ref = useRef<HTMLDivElement>(null);
  const ranking = useMemo(() => regionalRanking(rows, metric), [rows, metric]);
  const [offset, setOffset] = useState(0);
  const last = Math.max(0, ranking.length - 9);
  const start = Math.min(offset, last);
  const visible = useMemo(() => ranking.slice(start, start + 9), [ranking, start]);
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const chart = echarts.init(ref.current);
    const ranking = visible;
    const sources = [...new Set(ranking.flatMap(([, values]) => [...values.keys()]))].sort();
    chart.setOption({
      animation: false,
      tooltip: { trigger: 'axis', confine: true },
      legend: { show: false },
      grid: { left: 200, right: 80, top: 10, bottom: 35 },
      xAxis: { type: 'value', name: metric === 'clicks' ? 'Clicks' : 'AUD', axisLabel: { hideOverlap: true } },
      yAxis: {
        type: 'category',
        inverse: true,
        data: ranking.map(([name]) => name),
        axisLabel: { width: 185, overflow: 'break', lineHeight: 13 },
      },
      series: sources.map((source) => ({
        name: source,
        type: 'bar',
        stack: 'source',
        itemStyle: { color: dataSplitColour(source) },
        label: {
          show: source === sources[sources.length - 1],
          position: 'right',
          formatter: (point: { dataIndex: number }) =>
            [...ranking[point.dataIndex][1].values()]
              .reduce((sum, value) => sum + value, 0)
              .toLocaleString('en-AU', { maximumFractionDigits: metric === 'clicks' ? 0 : 2 }),
        },
        data: ranking.map(([, values]) => values.get(source) ?? 0),
      })),
    });
    const resize = new ResizeObserver(() => chart.resize());
    resize.observe(ref.current);
    return () => {
      resize.disconnect();
      chart.dispose();
    };
  }, [visible, metric]);
  const description = visible
    .map(
      ([name, values]) =>
        `${name}: ${[...values.values()].reduce((sum, value) => sum + value, 0).toLocaleString('en-AU')}`,
    )
    .join('; ');
  return (
    <Box>
      {!printMode && (
        <Typography sx={{ fontSize: 11 }}>
          {ranking.length ? `${start + 1}–${Math.min(start + 9, ranking.length)} of ${ranking.length}` : '0 groups'}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box
          ref={ref}
          role="img"
          aria-label={`${metric} ranking. ${description}. ${ranking.length} groups; scroll to see all.`}
          sx={{ height: ranking.length === 1 ? 150 : 390, flex: 1, minWidth: 0, bgcolor: reportPalette.white }}
        />
        {last > 0 && (
          <Slider
            orientation="vertical"
            min={0}
            max={last}
            value={last - start}
            onChange={(_, value) => setOffset(last - Number(value))}
            aria-label={`${metric} ranking scroll`}
            sx={{ height: 310, mt: 5, mr: 1 }}
          />
        )}
      </Box>
    </Box>
  );
}
